// Edit-habit bottom sheet — lets the user change a habit's color and/or
// frequency after creation.
//
// Design decisions
// ----------------
//  * Color edits are always visual-only → apply immediately, no prompt.
//    Past days keep their checked/unchecked state untouched; only the
//    accent used to render them changes.
//  * Frequency edits face a real choice. If we just overwrite frequency
//    the user loses control over history — e.g. flipping Daily → Weekdays
//    would instantly make every past Saturday look like an "off day" it
//    never was. So we ask on Save:
//      - "Apply to all history" → clobber the field (simple overwrite).
//      - "Only future days"     → keep the old schedule as an override
//        scoped to days BEFORE today, write the new schedule on the habit.
//    See habitScheduleOverrides.js for the storage shape, and
//    habitSchedule.js for how the two are reconciled on read.
//  * We deliberately don't edit name or icon here — the user asked for
//    "color and frequency", and a focused sheet is clearer than a
//    kitchen-sink edit form. Name/icon can still be changed by deleting
//    and recreating, and we can widen the scope later if requested.

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../components/Icon.jsx';
import { ACCENTS, resolveAccentColor } from '../lib/constants.js';
import { useTheme } from '../lib/themeStore.js';
import { useUpdateHabit } from '../lib/hooks.js';
import { setOverride, clearOverride, getOverride } from '../lib/habitScheduleOverrides.js';
import { todayIsoKey } from '../lib/dateKeys.js';
import { sfx } from '../lib/sfx.js';

const FREQS = ['Daily', 'Weekdays', 'Custom'];
const DAY_LABELS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const DAY_FULL   = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function EditHabitSheet({ open, habit, onClose }) {
  const updateHabit = useUpdateHabit();
  const theme = useTheme();

  // Seed local state from the habit when the sheet opens. `habit` can be
  // null between opens; we guard the reset so we don't stomp the user's
  // in-flight edits while the close animation is still playing.
  const [accent, setAccent] = useState('blue');
  const [freq, setFreq] = useState('Daily');
  const [customDays, setCustomDays] = useState([1, 2, 3, 4, 5]);
  const [pendingScope, setPendingScope] = useState(null);   // null | { freq, customDays }

  useEffect(() => {
    if (open && habit) {
      setAccent(habit.accent || 'blue');
      setFreq(habit.frequency || 'Daily');
      setCustomDays(Array.isArray(habit.customDays) && habit.customDays.length > 0
        ? habit.customDays
        : [1, 2, 3, 4, 5]);
      setPendingScope(null);
    }
  }, [open, habit?.id]); // eslint-disable-line

  if (!habit) return null;

  const accentColor = resolveAccentColor(accent, theme);

  // Detect what actually changed. We care about the distinction between
  // "only color" (apply immediately, no prompt) and "frequency changed"
  // (show the retro/forward choice).
  const colorChanged = accent !== (habit.accent || 'blue');
  const freqChanged = freq !== (habit.frequency || 'Daily');
  const customDaysChanged =
    freq === 'Custom' &&
    (habit.frequency !== 'Custom' ||
      !arraysEqual(
        [...(customDays || [])].sort((a, b) => a - b),
        [...(habit.customDays || [])].sort((a, b) => a - b)
      ));
  const scheduleChanged = freqChanged || customDaysChanged;

  const needsDays = freq === 'Custom' && customDays.length === 0;
  const anyChange = colorChanged || scheduleChanged;
  const canSave = anyChange && !needsDays;

  // Retroactive apply — overwrite the stored schedule and clear any
  // previously-set forward-only override (otherwise the old split would
  // keep applying to the new "past" days, which is the opposite of retro).
  const applyRetroactive = () => {
    const patch = {};
    if (colorChanged) patch.accent = accent;
    if (scheduleChanged) {
      patch.frequency = freq;
      patch.customDays = freq === 'Custom'
        ? [...customDays].sort((a, b) => a - b)
        : null;
    }
    clearOverride(habit.id);
    updateHabit.mutate({ id: habit.id, patch });
    sfx.check && sfx.check();
    onClose();
  };

  // Forward-only apply — store the current schedule as `prev*` with today
  // as the cutoff, then write the new schedule onto the habit. If there was
  // already an override in place, we keep its earliest prev* so we don't
  // lose history beyond the previous split.
  const applyForwardOnly = () => {
    const patch = { accent: colorChanged ? accent : habit.accent };
    patch.frequency = freq;
    patch.customDays = freq === 'Custom'
      ? [...customDays].sort((a, b) => a - b)
      : null;

    const existing = getOverride(habit.id);
    // If an older split already exists, don't clobber it — its prev* is
    // older truth than the habit's current fields.
    setOverride(habit.id, {
      prevFrequency: existing?.prevFrequency || habit.frequency || 'Daily',
      prevCustomDays: existing?.prevCustomDays
        ?? (Array.isArray(habit.customDays) ? habit.customDays : null),
      frequencyChangedAt: todayIsoKey(),
    });
    updateHabit.mutate({ id: habit.id, patch });
    sfx.check && sfx.check();
    onClose();
  };

  const onSave = () => {
    if (!canSave) return;
    // Color-only change: no prompt, apply now.
    if (!scheduleChanged) {
      applyRetroactive();
      return;
    }
    // Schedule change: ask how to scope it. We reuse the same backdrop
    // by swapping the sheet body into a scope-picker rather than pushing a
    // second modal.
    setPendingScope({ freq, customDays });
  };

  // EditHabitSheet is called from inside Dashboard, which lives inside
  // `div.scroll`. `.scroll` has `position:relative; z-index:2` which makes it
  // a stacking context — everything it contains, including our sheet at
  // z-index 201, gets clipped behind any sibling of `.scroll` with a higher
  // z-index. The tab bar (z-index 40) is such a sibling, so without a
  // portal the bottom of this sheet ends up visually covered by the tab
  // bar on iOS. Portalling to `.screen` (the phone-frame container) puts
  // us on the same stacking level as the tab bar, and our z-index 201
  // wins cleanly. AddHabitSheet has no such bug because it's rendered
  // directly in AppShell as a sibling of the tab bar.
  const portalTarget = typeof document !== 'undefined'
    ? (document.querySelector('.screen') || document.body)
    : null;

  const body = (
    <>
      <div className={'sheet-backdrop' + (open ? ' on' : '')} onClick={onClose}/>
      <div className={'sheet' + (open ? ' on' : '')}>
        <div className="sheet-grabber"/>
        <div style={{ padding: '14px 20px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button onClick={onClose} style={cancelBtnStyle}>Cancel</button>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 15, color: 'var(--fg-1)' }}>
            {pendingScope ? 'Apply frequency change' : `Edit ${habit.name}`}
          </div>
          {!pendingScope ? (
            <button
              onClick={onSave}
              disabled={!canSave}
              style={{
                background: 'transparent', border: 'none',
                color: canSave ? 'var(--accent)' : 'var(--fg-4)',
                fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 14,
                cursor: canSave ? 'pointer' : 'not-allowed', padding: 0,
              }}
            >Save</button>
          ) : (
            <button onClick={() => setPendingScope(null)} style={cancelBtnStyle}>Back</button>
          )}
        </div>

        {pendingScope ? (
          <ScopePicker
            habit={habit}
            nextFreq={pendingScope.freq}
            nextCustomDays={pendingScope.customDays}
            accentColor={accentColor}
            onRetro={applyRetroactive}
            onForward={applyForwardOnly}
          />
        ) : (
          <div style={{ padding: '6px 20px 28px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 18 }}>
            {/* Preview — same treatment as AddHabitSheet so the user sees
                exactly what the change will produce. */}
            <div style={{
              padding: 14, borderRadius: 16,
              background: `linear-gradient(180deg, ${accentColor}14, transparent)`,
              border: '1px solid var(--stroke-1)',
              display: 'flex', gap: 12, alignItems: 'center',
            }}>
              <div style={{
                width: 40, height: 40, borderRadius: 11,
                background: `linear-gradient(135deg, ${accentColor}35, ${accentColor}10)`,
                border: `1px solid ${accentColor}55`,
                display: 'grid', placeItems: 'center',
                boxShadow: `0 0 14px ${accentColor}44`,
              }}>
                <Icon name={habit.icon} size={18} color={accentColor}/>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 14, color: 'var(--fg-1)' }}>
                  {habit.name}
                </div>
                <div style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--fg-3)', marginTop: 2 }}>
                  {freq} · Midnight reset
                </div>
              </div>
            </div>

            <div>
              <div className="eyebrow" style={{ marginBottom: 8 }}>Accent</div>
              <div style={{ display: 'flex', gap: 10 }}>
                {Object.keys(ACCENTS).map(k => {
                  const c = resolveAccentColor(k, theme);
                  return (
                    <div
                      key={k}
                      className={'color-chip' + (accent === k ? ' on' : '')}
                      onClick={() => setAccent(k)}
                      style={{
                        background: `linear-gradient(135deg, ${c}, ${c}AA)`,
                        boxShadow: accent === k ? `0 0 14px ${c}99` : 'none',
                      }}
                      title={ACCENTS[k].name}
                    />
                  );
                })}
              </div>
            </div>

            <div>
              <div className="eyebrow" style={{ marginBottom: 8 }}>Frequency</div>
              <div style={{ display: 'flex', gap: 6 }}>
                {FREQS.map(f => (
                  <button key={f} onClick={() => setFreq(f)} className={'chip-opt' + (freq === f ? ' on' : '')} style={{ flex: 1 }}>
                    {f}
                  </button>
                ))}
              </div>
              {freq === 'Custom' && (
                <div style={{ marginTop: 10 }}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {DAY_LABELS.map((lbl, i) => {
                      const on = customDays.includes(i);
                      return (
                        <button
                          key={i}
                          onClick={() => {
                            sfx.check && sfx.check();
                            setCustomDays(prev => prev.includes(i) ? prev.filter(x => x !== i) : [...prev, i]);
                          }}
                          aria-pressed={on}
                          aria-label={DAY_FULL[i]}
                          style={{
                            flex: 1, height: 36,
                            borderRadius: 10,
                            background: on
                              ? `linear-gradient(180deg, ${accentColor}33, ${accentColor}14)`
                              : 'rgba(255,255,255,0.03)',
                            border: on
                              ? `1px solid ${accentColor}66`
                              : '1px solid var(--stroke-1)',
                            color: on ? '#fff' : 'var(--fg-3)',
                            fontFamily: 'var(--font-display)',
                            fontWeight: 700, fontSize: 13,
                            letterSpacing: '0.02em',
                            cursor: 'pointer',
                            transition: 'all 140ms var(--ease-out)',
                            boxShadow: on ? `0 0 10px ${accentColor}44, inset 0 1px 0 rgba(255,255,255,0.1)` : 'none',
                          }}
                        >
                          {lbl}
                        </button>
                      );
                    })}
                  </div>
                  <div style={{
                    marginTop: 8,
                    fontFamily: 'var(--font-body)', fontSize: 11.5,
                    color: customDays.length === 0 ? '#ff8a8a' : 'var(--fg-4)',
                    lineHeight: 1.4,
                  }}>
                    {customDays.length === 0
                      ? 'Pick at least one day'
                      : customDays.length === 7
                        ? 'Every day'
                        : [...customDays].sort((a,b)=>a-b).map(i => DAY_FULL[i]).join(' · ')}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );

  return portalTarget ? createPortal(body, portalTarget) : body;
}

// Scope picker — shown after Save when the schedule actually changed.
// Explains each choice in one sentence so the analytic consequence is
// visible before commit.
function ScopePicker({ habit, nextFreq, nextCustomDays, accentColor, onRetro, onForward }) {
  const oldLabel = describeSchedule(habit.frequency, habit.customDays);
  const newLabel = describeSchedule(nextFreq, nextCustomDays);
  return (
    <div style={{ padding: '6px 20px 28px', display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{
        padding: 14, borderRadius: 14,
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid var(--stroke-1)',
      }}>
        <div style={{ fontFamily: 'var(--font-body)', fontSize: 12.5, color: 'var(--fg-3)', lineHeight: 1.5 }}>
          Changing <b style={{ color: 'var(--fg-1)' }}>{habit.name}</b> from
          {' '}<span style={{ color: 'var(--fg-1)' }}>{oldLabel}</span> to
          {' '}<span style={{ color: accentColor }}>{newLabel}</span>.
        </div>
      </div>

      <ScopeButton
        title="Apply to all history"
        sub="The new schedule rewrites past days. Analytics and streaks recompute as if this was always the rule."
        onClick={onRetro}
      />
      <ScopeButton
        title="Only future days"
        sub="Past days keep counting under the old schedule. The new rule takes effect from today."
        emphasized
        accentColor={accentColor}
        onClick={onForward}
      />
    </div>
  );
}

function ScopeButton({ title, sub, emphasized, accentColor, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        textAlign: 'left',
        padding: '14px 16px',
        borderRadius: 14,
        background: emphasized
          ? `linear-gradient(180deg, ${accentColor}22, ${accentColor}08)`
          : 'rgba(255,255,255,0.04)',
        border: emphasized
          ? `1px solid ${accentColor}66`
          : '1px solid var(--stroke-1)',
        cursor: 'pointer',
        display: 'flex', flexDirection: 'column', gap: 4,
      }}
    >
      <div style={{
        fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 14,
        color: 'var(--fg-1)',
      }}>{title}</div>
      <div style={{
        fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--fg-3)',
        lineHeight: 1.45,
      }}>{sub}</div>
    </button>
  );
}

function describeSchedule(freq, customDays) {
  if (freq === 'Daily') return 'Daily';
  if (freq === 'Weekdays') return 'Weekdays';
  if (freq === 'Custom') {
    const days = Array.isArray(customDays) ? [...customDays].sort((a, b) => a - b) : [];
    if (days.length === 0) return 'Custom';
    if (days.length === 7) return 'Every day';
    return days.map(i => DAY_FULL[i]).join(' · ');
  }
  return freq;
}

function arraysEqual(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

const cancelBtnStyle = {
  background: 'transparent', border: 'none',
  color: 'var(--fg-3)', fontFamily: 'var(--font-display)',
  fontWeight: 600, fontSize: 14, cursor: 'pointer', padding: 0,
};
